import type { ViewGroup, NavigationResult, SafeNavigationResult } from './types';

export const SafeNavigation = {
  getFirstView: (group: ViewGroup | null | undefined) => {
    if (!group?.views?.length) {
      return null;
    }
    return group.views[0] || null;
  },

  createNavigation: (
    group: ViewGroup | null | undefined,
    routeName: string,
    params: Record<string, any>,
    title: string,
    fallback?: NavigationResult
  ): SafeNavigationResult<NavigationResult> => {
    const firstView = SafeNavigation.getFirstView(group);

    if (!firstView || !group) {
      return {
        success: false,
        error: `No views available in group ${group?.id || 'unknown'}`,
        fallback,
      };
    }

    return {
      success: true,
      data: {
        route: {
          name: routeName,
          params: {
            ...params,
            viewId: firstView.id,
            groupId: group.id,
          },
        },
        title,
      },
    };
  },

  validateGroupHasViews: (
    group: ViewGroup | null | undefined
  ): group is ViewGroup & { views: { 0: any } } => {
    return !!group?.views?.length;
  },

  isNavigableGroup: (group: ViewGroup | null | undefined): boolean => {
    if (!group) return false;

    if (!group.is_skipable) {
      return true;
    }

    return SafeNavigation.validateGroupHasViews(group);
  },

  findFirstValidGroup: (groups: ViewGroup[] | null | undefined) => {
    if (!groups?.length) {
      return null;
    }
    return groups.find((group) => SafeNavigation.validateGroupHasViews(group)) || null;
  },

  getSafeRoute: (
    primaryGroup: ViewGroup | null | undefined,
    routeName: string,
    params: Record<string, any>,
    title: string,
    fallbackRoute: { name: string; params: Record<string, any>; title: string }
  ): NavigationResult => {
    const result = SafeNavigation.createNavigation(primaryGroup, routeName, params, title);

    if (result.success) {
      return result.data;
    }

    return {
      route: {
        name: fallbackRoute.name,
        params: fallbackRoute.params,
      },
      title: fallbackRoute.title,
    };
  },

  hasAnyValidViews: (groups: ViewGroup[] | null | undefined): boolean => {
    return !!groups?.some((group) => SafeNavigation.validateGroupHasViews(group));
  },

  getValidGroups: (groups: ViewGroup[] | null | undefined): ViewGroup[] => {
    if (!groups?.length) {
      return [];
    }
    return groups.filter((group) => SafeNavigation.validateGroupHasViews(group));
  },

  getNavigableGroups: (groups: ViewGroup[] | null | undefined): ViewGroup[] => {
    if (!groups?.length) {
      return [];
    }
    return groups.filter((group) => SafeNavigation.isNavigableGroup(group));
  },

  validateSegmentForNavigation: (segment: any): boolean => {
    if (!segment?.view_groups?.length) {
      return false;
    }
    return SafeNavigation.hasAnyValidViews(segment.view_groups);
  },
};

export const NavigationFallbacks = {
  SEGMENT_SELECTION: 'SelectSegmentView',
  CALCULATION_SUMMARY: 'CalculationSummaryView',
} as const;
